// src/pages/PerformanceLab.jsx
// Deep performance analysis and improvement planning
// Pro feature — free users see upgrade prompt
import React, { useState, useRef, useCallback } from 'react';
import { Upload, FlaskConical, Target, ChevronDown, ChevronUp, CheckCircle, AlertTriangle, Info, TrendingUp, Sparkles, BarChart3, AlertOctagon } from 'lucide-react';
import { parseCSV, detectFormat, mapODFRow, mapMT4Row, validateTrade, deduplicateTrades, buildColumnMapper } from '../utils/csvImport';
import { runDiagnostic } from '../utils/diagnosticEngine';
import { usePlan } from '../hooks/usePlan';
import ProGate from '../components/ProGate';

const G = '#00e5a0';

function StatusIcon({ status }) {
  if (status === 'ok' || status === 'positive') return <CheckCircle size={16} style={{ color:G, flexShrink:0 }} />;
  if (status === 'critical' || status === 'warning') return <AlertTriangle size={16} style={{ color: status==='critical'?'#ff4d6d':'#ffd166', flexShrink:0 }} />;
  return <Info size={16} style={{ color:'#4cc9f0', flexShrink:0 }} />;
}

function QuestionCard({ q, index }) {
  const [open, setOpen] = useState(index === 0);
  const borderColor = q.status==='critical'?'rgba(255,77,109,0.3)': q.status==='warning'?'rgba(255,209,102,0.2)': q.status==='ok'?'rgba(0,229,160,0.15)':'rgba(76,201,240,0.15)';

  return (
    <div style={{ background:'#0d0d14', border:`1px solid ${borderColor}`, borderRadius:12, overflow:'hidden', marginBottom:10 }}>
      <button onClick={() => setOpen(o=>!o)} style={{ width:'100%', display:'flex', alignItems:'center', gap:12, padding:'14px 18px', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
        <div style={{ width:22, height:22, borderRadius:'50%', background:'rgba(255,255,255,0.05)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", flexShrink:0 }}>
          {index+1}
        </div>
        <StatusIcon status={q.status} />
        <div style={{ flex:1, fontSize:13, fontWeight:600, color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif" }}>{q.question}</div>
        {open ? <ChevronUp size={14} style={{ color:'#55556a' }} /> : <ChevronDown size={14} style={{ color:'#55556a' }} />}
      </button>

      {open && (
        <div style={{ padding:'0 18px 18px' }}>
          <div style={{ fontSize:13, color:'#f0f0f5', lineHeight:1.8, marginBottom:14, fontStyle:'italic', borderLeft:`3px solid ${borderColor}`, paddingLeft:12 }}>
            {q.verdict}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {(q.findings||[]).map((f,i) => (
              <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', fontSize:12, color:'#8888a8', lineHeight:1.6 }}>
                <span style={{ color: f.severity==='positive'?G: f.severity==='warning'?'#ffd166': f.severity==='high'?'#ff4d6d':'#4cc9f0', flexShrink:0, marginTop:2 }}>
                  {f.severity==='positive'?'✓':f.severity==='high'?'✗':'→'}
                </span>
                {f.text}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PerformanceLab({ trades, settings, onImport , lang = 'en'}) {
  const { isPro, can } = usePlan();
  const [tab,        setTab]        = useState('import');
  const [importing,  setImporting]  = useState(false);
  const [preview,    setPreview]    = useState(null);
  const [mapMode,    setMapMode]    = useState(false);
  const [columnMap,  setColumnMap]  = useState({});
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows,    setRawRows]    = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [dragOver,   setDragOver]   = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [planSaved,  setPlanSaved]  = useState(false);
  const fileRef = useRef(null);

  const processFile = useCallback((file) => {
    if (!file || !file.name.endsWith('.csv')) { alert('Please upload a .csv file'); return; }
    const reader = new FileReader();
    reader.onload = e => {
      const { headers, rows } = parseCSV(e.target.result);
      if (!headers.length || !rows.length) { alert('CSV appears empty or invalid.'); return; }
      const format = detectFormat(headers);
      setRawHeaders(headers); setRawRows(rows);
      if (format === 'odftrades') {
        const mapped = rows.map(mapODFRow);
        const errors = []; mapped.forEach((t,i) => errors.push(...validateTrade(t,i)));
        const { unique, skipped } = deduplicateTrades(mapped, trades);
        setPreview({ trades:unique, format:'odftrades', errors, skipped });
        setMapMode(false);
      } else if (format === 'mt4') {
        const mapped = rows.map(mapMT4Row);
        const errors = []; mapped.forEach((t,i) => errors.push(...validateTrade(t,i)));
        const { unique, skipped } = deduplicateTrades(mapped, trades);
        setPreview({ trades:unique, format:'mt4', errors, skipped });
        setMapMode(false);
      } else {
        const { fields, suggestions } = buildColumnMapper(headers);
        setColumnMap(suggestions); setMapMode(true);
        setPreview({ trades:[], format:'unknown', errors:[], skipped:[], fields, headers });
      }
    };
    reader.readAsText(file);
  }, [trades]);

  const confirmImport = async () => {
    if (!preview?.trades?.length) return;
    setImporting(true);
    let imported = 0, failed = 0;
    for (const trade of preview.trades.filter(t => t.date && t.pair && ['Win','Loss','Breakeven'].includes(t.result))) {
      try { await onImport(trade); imported++; } catch { failed++; }
    }
    setImportResult({ imported, failed, skipped: preview.skipped?.length||0 });
    setPreview(null);
    setImporting(false);
    // Auto-run diagnostic after import if Pro
    if (isPro && imported > 0) { setTimeout(() => { setTab('diagnostic'); setDiagnostic(null); }, 500); }
  };

  const runAnalysis = () => {
    const result = runDiagnostic(trades, settings);
    setDiagnostic(result);
  };

  const inp = { background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:7, color:'#f0f0f5', padding:'7px 10px', fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", outline:'none', width:'100%', boxSizing:'border-box' };

  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:960, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
          <h1 style={{ fontSize:22, fontWeight:600, color:'#f0f0f5', margin:0, fontFamily:"'IBM Plex Sans',sans-serif" }}>Performance Lab</h1>
          {isPro && <div style={{ fontSize:10, color:G, background:'rgba(0,229,160,0.1)', border:'1px solid rgba(0,229,160,0.2)', borderRadius:5, padding:'2px 8px', fontFamily:"'DM Mono',monospace" }}>PRO</div>}
        </div>
        <p style={{ color:'#55556a', fontSize:13, margin:0, fontFamily:"'DM Mono',monospace" }}>
          Import · Diagnose · Improve
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:24, flexWrap:'wrap' }}>
        {[
          { id:'import',     icon:Upload,      label:'Import Data',        free:true },
          { id:'diagnostic', icon:FlaskConical, label:'Diagnostic Report',  free:false },
          { id:'plan',       icon:Target,       label:'Improvement Plan',   free:false },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            display:'flex', alignItems:'center', gap:7,
            padding:'9px 18px', borderRadius:8, border:'1px solid', fontSize:13,
            cursor:'pointer', outline:'none', fontFamily:"'IBM Plex Sans',sans-serif",
            background: tab===t.id ? 'rgba(0,229,160,0.1)' : 'transparent',
            borderColor: tab===t.id ? 'rgba(0,229,160,0.3)' : '#1e1e2e',
            color: tab===t.id ? G : '#8888a8', fontWeight: tab===t.id ? 600 : 400,
          }}>
            <t.icon size={13} />
            {t.label}
            {!t.free && !isPro && <div style={{ fontSize:9, color:'#ffd166', background:'rgba(255,209,102,0.1)', border:'1px solid rgba(255,209,102,0.2)', borderRadius:4, padding:'1px 5px', fontFamily:"'DM Mono',monospace" }}>PRO</div>}
          </button>
        ))}
      </div>

      {/* ── IMPORT TAB ── */}
      {tab === 'import' && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {importResult && (
            <div style={{ background:'rgba(0,229,160,0.06)', border:'1px solid rgba(0,229,160,0.2)', borderRadius:12, padding:16, display:'flex', alignItems:'center', gap:14 }}>
              <CheckCircle size={20} style={{ color:G }} />
              <div>
                <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5', marginBottom:2 }}>Import complete</div>
                <div style={{ fontSize:12, color:'#8888a8' }}>{importResult.imported} trades imported{importResult.skipped>0?` · ${importResult.skipped} duplicates skipped`:''}{importResult.failed>0?` · ${importResult.failed} failed`:''}</div>
              </div>
              {isPro && <button onClick={() => { setTab('diagnostic'); setImportResult(null); }} style={{ marginLeft:'auto', background:G, color:'#0a0a0f', border:'none', borderRadius:7, padding:'8px 16px', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Run Diagnostic →</button>}
            </div>
          )}

          {!preview && !mapMode && (
            <div onDragOver={e=>{e.preventDefault();setDragOver(true)}} onDragLeave={()=>setDragOver(false)}
              onDrop={e=>{e.preventDefault();setDragOver(false);processFile(e.dataTransfer.files[0])}}
              onClick={() => fileRef.current?.click()}
              style={{ background:dragOver?'rgba(0,229,160,0.06)':'#16161f', border:`2px dashed ${dragOver?'rgba(0,229,160,0.4)':'#2a2a3e'}`, borderRadius:14, padding:48, textAlign:'center', cursor:'pointer', transition:'all 0.2s' }}>
              <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={e=>processFile(e.target.files[0])} />
              <Upload size={32} style={{ color:dragOver?G:'#2a2a3e', marginBottom:16 }} />
              <div style={{ fontSize:15, fontWeight:600, color:dragOver?G:'#f0f0f5', marginBottom:8 }}>Drop your broker CSV here</div>
              <div style={{ fontSize:13, color:'#55556a', marginBottom:16 }}>or click to browse</div>
              <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                {['MT4 / MT5','TradingView','NinjaTrader','Tradovate','Interactive Brokers','ODFTrades CSV','Any CSV'].map(f=>(
                  <span key={f} style={{ fontSize:11, color:'#55556a', background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:5, padding:'3px 9px', fontFamily:"'DM Mono',monospace" }}>{f}</span>
                ))}
              </div>
            </div>
          )}

          {mapMode && preview && (
            <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:24 }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5', marginBottom:6 }}>Map Your Columns</div>
              <div style={{ fontSize:13, color:'#8888a8', marginBottom:20 }}>Match your CSV headers to ODFTrades fields.</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:12, marginBottom:20 }}>
                {preview.fields?.map(field => (
                  <div key={field.key}>
                    <label style={{ fontSize:11, color:field.required?G:'#8888a8', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:5 }}>{field.label}{field.required?' *':''}</label>
                    <select value={columnMap[field.key]||''} onChange={e=>setColumnMap(m=>({...m,[field.key]:e.target.value}))} style={inp}>
                      <option value="">— Not mapped —</option>
                      {preview.headers?.map(h=><option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => {
                  const mapped = rawRows.map(row => { const t={}; for(const[k,v] of Object.entries(columnMap)) { if(v&&row[v]!==undefined) t[k]=row[v]; } return mapODFRow(t); });
                  const errors=[]; mapped.forEach((t,i)=>errors.push(...validateTrade(t,i)));
                  const {unique,skipped}=deduplicateTrades(mapped,trades);
                  setPreview({trades:unique,format:'custom',errors,skipped}); setMapMode(false);
                }} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:8, padding:'10px 20px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Preview Import →</button>
                <button onClick={()=>{setMapMode(false);setPreview(null);}} style={{ background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e', borderRadius:8, padding:'10px 16px', fontSize:13, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Cancel</button>
              </div>
            </div>
          )}

          {preview && !mapMode && (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:12, padding:'16px 20px', display:'flex', gap:20, flexWrap:'wrap', alignItems:'center' }}>
                <div><div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', marginBottom:3 }}>Format</div><div style={{ fontSize:13, fontWeight:600, color: preview.format==='mt4'?'#4cc9f0':G }}>{preview.format==='mt4'?'MT4/MT5':'ODFTrades CSV'}</div></div>
                <div><div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', marginBottom:3 }}>Ready</div><div style={{ fontSize:22, fontWeight:700, color:G, fontFamily:"'DM Mono',monospace" }}>{preview.trades.length}</div></div>
                {preview.skipped?.length>0 && <div><div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', marginBottom:3 }}>Duplicates</div><div style={{ fontSize:22, fontWeight:700, color:'#ffd166', fontFamily:"'DM Mono',monospace" }}>{preview.skipped.length}</div></div>}
              </div>
              {preview.trades.length>0 && (
                <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:12, overflow:'hidden' }}>
                  <div style={{ padding:'12px 18px', borderBottom:'1px solid #1e1e2e', fontSize:12, fontWeight:600, color:'#f0f0f5' }}>Preview — first 5 trades</div>
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                      <thead><tr style={{ background:'#0d0d14' }}>{['Date','Pair','Dir','Result','P&L','RR'].map(h=><th key={h} style={{ padding:'7px 12px', textAlign:'left', color:'#55556a', fontFamily:"'DM Mono',monospace", fontSize:10, textTransform:'uppercase' }}>{h}</th>)}</tr></thead>
                      <tbody>{preview.trades.slice(0,5).map((t,i)=>(
                        <tr key={i} style={{ borderTop:'1px solid #1e1e2e' }}>
                          <td style={{ padding:'8px 12px', color:'#8888a8', fontFamily:"'DM Mono',monospace", fontSize:11 }}>{t.date}</td>
                          <td style={{ padding:'8px 12px', color:'#f0f0f5', fontWeight:500 }}>{t.pair}</td>
                          <td style={{ padding:'8px 12px', color:t.direction==='Buy'?G:'#ff4d6d' }}>{t.direction}</td>
                          <td style={{ padding:'8px 12px', color:t.result==='Win'?G:t.result==='Loss'?'#ff4d6d':'#ffd166', fontSize:11 }}>{t.result}</td>
                          <td style={{ padding:'8px 12px', color:Number(t.pnl||0)>=0?G:'#ff4d6d', fontFamily:"'DM Mono',monospace" }}>{Number(t.pnl||0)>=0?'+':''}${Number(t.pnl||0).toFixed(2)}</td>
                          <td style={{ padding:'8px 12px', color:'#8888a8', fontFamily:"'DM Mono',monospace" }}>{t.rrAchieved||'—'}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                    {preview.trades.length>5 && <div style={{ padding:'8px 18px', fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", borderTop:'1px solid #1e1e2e' }}>+{preview.trades.length-5} more trades</div>}
                  </div>
                </div>
              )}
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={confirmImport} disabled={importing||!preview.trades.length} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:9, padding:'12px 24px', fontSize:14, fontWeight:700, cursor:importing?'not-allowed':'pointer', fontFamily:"'IBM Plex Sans',sans-serif", opacity:importing||!preview.trades.length?0.6:1 }}>
                  {importing?'Importing...':`Confirm Import (${preview.trades.length} trades)`}
                </button>
                <button onClick={()=>{setPreview(null);if(fileRef.current)fileRef.current.value='';}} style={{ background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e', borderRadius:9, padding:'12px 16px', fontSize:13, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── DIAGNOSTIC TAB ── */}
      {tab === 'diagnostic' && (
        <ProGate feature="Performance Diagnostic" isPro={isPro}>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {trades.filter(t=>t.status!=='open').length < 10 ? (
              <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:32, textAlign:'center' }}>
                <div style={{ display:'flex', justifyContent:'center', marginBottom:14 }}>
                  <div style={{ width:52, height:52, borderRadius:14, background:'rgba(255,255,255,0.03)', border:'1px solid #1e1e2e', display:'flex', alignItems:'center', justifyContent:'center', color:'#55556a' }}><BarChart3 size={24} strokeWidth={1.6} /></div>
                </div>
                <div style={{ fontSize:15, fontWeight:600, color:'#f0f0f5', marginBottom:8 }}>Need more trades</div>
                <div style={{ fontSize:13, color:'#8888a8' }}>Log or import at least 10 closed trades to run the diagnostic.</div>
              </div>
            ) : !diagnostic ? (
              <div style={{ background:'#16161f', border:'1px solid rgba(0,229,160,0.15)', borderRadius:14, padding:32, textAlign:'center' }}>
                <FlaskConical size={40} style={{ color:G, marginBottom:16 }} />
                <div style={{ fontSize:16, fontWeight:600, color:'#f0f0f5', marginBottom:8 }}>7-Question Performance Diagnostic</div>
                <div style={{ fontSize:13, color:'#8888a8', marginBottom:24, lineHeight:1.7, maxWidth:480, margin:'0 auto 24px' }}>
                  Analyses your {trades.filter(t=>t.status!=='open').length} closed trades across 7 dimensions to identify exactly what is causing losses and what needs to change.
                </div>
                <button onClick={runAnalysis} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:10, padding:'13px 32px', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>
                  Run Diagnostic →
                </button>
              </div>
            ) : (
              <>
                {/* Verdict banner */}
                <div style={{ background:`${diagnostic.overallVerdict.color}10`, border:`1px solid ${diagnostic.overallVerdict.color}25`, borderRadius:12, padding:'16px 20px', display:'flex', gap:14, alignItems:'center' }}>
                  <div style={{ color: diagnostic.overallVerdict.color, display:'flex', alignItems:'center' }}>
                    {diagnostic.overallVerdict.level === 'critical' ? <AlertOctagon  size={26} strokeWidth={1.7} />
                   : diagnostic.overallVerdict.level === 'warning'  ? <AlertTriangle size={26} strokeWidth={1.7} />
                   : diagnostic.overallVerdict.level === 'good'     ? <CheckCircle   size={26} strokeWidth={1.7} />
                   :                                                  <BarChart3     size={26} strokeWidth={1.7} />}
                  </div>
                  <div>
                    <div style={{ fontSize:15, fontWeight:600, color:diagnostic.overallVerdict.color, marginBottom:3 }}>{diagnostic.overallVerdict.label}</div>
                    <div style={{ fontSize:13, color:'#8888a8', lineHeight:1.6 }}>{diagnostic.overallVerdict.summary}</div>
                  </div>
                  <button onClick={() => { setTab('plan'); }} style={{ marginLeft:'auto', background:G, color:'#0a0a0f', border:'none', borderRadius:8, padding:'9px 18px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", flexShrink:0 }}>
                    View Plan →
                  </button>
                </div>

                {/* 7 questions */}
                {diagnostic.questions.map((q,i) => <QuestionCard key={q.id} q={q} index={i} />)}

                <button onClick={() => setDiagnostic(null)} style={{ background:'transparent', color:'#55556a', border:'1px solid #1e1e2e', borderRadius:8, padding:'10px', fontSize:12, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", alignSelf:'flex-start' }}>
                  ↺ Re-run Analysis
                </button>
              </>
            )}
          </div>
        </ProGate>
      )}

      {/* ── PLAN TAB ── */}
      {tab === 'plan' && (
        <ProGate feature="Improvement Plan" isPro={isPro}>
          {!diagnostic ? (
            <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:32, textAlign:'center' }}>
              <Target size={40} style={{ color:'#ffd166', marginBottom:16 }} />
              <div style={{ fontSize:15, fontWeight:600, color:'#f0f0f5', marginBottom:8 }}>Run the diagnostic first</div>
              <div style={{ fontSize:13, color:'#8888a8', marginBottom:20 }}>Your improvement plan is generated from your diagnostic results.</div>
              <button onClick={() => setTab('diagnostic')} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:9, padding:'11px 24px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Go to Diagnostic →</button>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:'20px 24px' }}>
                <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Your 30-Day Focus</div>
                <div style={{ fontSize:18, fontWeight:600, color:'#f0f0f5', marginBottom:4, fontFamily:"'IBM Plex Sans',sans-serif" }}>3 Things That Will Move the Needle</div>
                <div style={{ fontSize:13, color:'#8888a8' }}>Based on your data — not generic advice. These specific changes have the highest impact on your results.</div>
              </div>

              {(diagnostic.plan?.focusAreas||[]).map((area, i) => (
                <div key={area.id} style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:'22px 24px', borderLeft:`3px solid ${i===0?G:i===1?'#ffd166':'#4cc9f0'}` }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:14 }}>
                    <span style={{ fontSize:24 }}>{area.icon}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:5, padding:'2px 8px' }}>FOCUS {i+1}</div>
                      </div>
                      <div style={{ fontSize:15, fontWeight:600, color:'#f0f0f5', marginBottom:6, fontFamily:"'IBM Plex Sans',sans-serif" }}>{area.title}</div>
                      <div style={{ fontSize:13, color:'#8888a8', lineHeight:1.7, marginBottom:12 }}>{area.action}</div>
                      <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                        <div style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:8, padding:'10px 14px' }}>
                          <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', marginBottom:3 }}>Current</div>
                          <div style={{ fontSize:14, fontWeight:600, color:'#ff4d6d', fontFamily:"'DM Mono',monospace" }}>{area.current}</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', color:'#55556a', fontSize:18 }}>→</div>
                        <div style={{ background:'#0a0a0f', border:'1px solid rgba(0,229,160,0.2)', borderRadius:8, padding:'10px 14px' }}>
                          <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', marginBottom:3 }}>Target</div>
                          <div style={{ fontSize:14, fontWeight:600, color:G, fontFamily:"'DM Mono',monospace" }}>{area.target_val}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {diagnostic.plan?.focusAreas?.length === 0 && (
                <div style={{ background:'rgba(0,229,160,0.05)', border:'1px solid rgba(0,229,160,0.15)', borderRadius:12, padding:24, textAlign:'center' }}>
                  <div style={{ display:'flex', justifyContent:'center', marginBottom:14 }}>
                    <div style={{ width:48, height:48, borderRadius:12, background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', color:G }}><Target size={22} strokeWidth={1.6} /></div>
                  </div>
                  <div style={{ fontSize:15, fontWeight:600, color:G, marginBottom:8 }}>No critical issues found</div>
                  <div style={{ fontSize:13, color:'#8888a8' }}>Your fundamentals are solid. Focus on increasing trade frequency within your identified edge.</div>
                </div>
              )}

              <div style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:10, padding:'14px 18px', fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", lineHeight:1.7 }}>
                Plan based on {diagnostic.tradeCount} closed trades · Self-reported data · Not financial advice · Re-run diagnostic after 20+ new trades for updated plan
              </div>
            </div>
          )}
        </ProGate>
      )}
    </div>
  );
}
